import { Component, OnDestroy } from '@angular/core';
import { FormGroup, UntypedFormBuilder, Validators } from '@angular/forms';
import { ActivatedRoute, ActivatedRouteSnapshot, Router } from '@angular/router';
import { AppRoutingService } from '@services/app-routing/app-routing.service';
import { CustomTranslateService } from '@services/common/custom-translate.service';
import { ToastService } from '@services/common/toast.service';
import { UserHttpService } from '@services/datasources/user-http.service';
import { User } from 'app/auth/models';
import { AuthenticationService } from 'app/auth/service';
import { Auth } from 'aws-amplify';
import { Observable, Subject, first, map, mergeMap, of, switchMap, tap } from 'rxjs';

@Component({
    selector: 'app-verification',
    templateUrl: './verification.component.html',
    styleUrls: ['./verification.component.scss'],
})
export class VerificationComponent implements OnDestroy {
    formGroup: FormGroup;
    email: string;
    currentError: 'unexpected' | 'invalidCode' | undefined;
    loading: boolean = false;

    constructor(
        private _authService: AuthenticationService,
        private _router: Router,
        private _route: ActivatedRoute,
        private _formBuilder: UntypedFormBuilder,
        private _toastService: ToastService,
        private translateService: CustomTranslateService,
        private routingService: AppRoutingService,
    ) {
        if (this._authService.getUserToValidate() === undefined) {
            const goBackUrl = this._router.lastSuccessfulNavigation?.finalUrl;
            if (goBackUrl === undefined || goBackUrl?.toString() === this._router.url) {
                this._router.navigate(['auth', 'register'], { relativeTo: this._route.root });
            } else this._router.navigateByUrl(goBackUrl);
        }
    }
    ngOnDestroy(): void {
        this._authService.endValidation();
    }

    ngOnInit(): void {
        this.initializeForm();
        const fullEmail = this._authService.getUserToValidate()?.username ?? 'michel.drucker@bar.com';
        const [user, domain] = fullEmail.split('@');
        const censoredUser = user.substring(0, 2) + '*'.repeat(4);
        this.email = `${censoredUser}@${domain}`;
    }

    onSubmit() {
        if (!this.formGroup.valid) return; // failsafe

        const code = this.formGroup.get('code')!.value!;
        const userToValidate = this._authService.getUserToValidate()!;
        this.loading = true;
        this._authService.confirmRegistration(userToValidate.username, code).subscribe({
            next: () => {
                this._authService.login(userToValidate.username, userToValidate.password).subscribe({
                    next: () => {
                        this.translateService.get('authentication.verification.success').subscribe((msg) => {
                            this._toastService.showSuccess({ details: msg as string });
                            this.loading = false;
                            this._router.navigateByUrl('/');
                        });
                    },
                    error: () => {
                        this._authService.logout(false).subscribe(() => {
                            this._router.navigateByUrl('/auth/login').then(() =>
                                this.translateService
                                    .get('authentication.verification.errors.initFailed')
                                    .subscribe((msg) => {
                                        this._toastService.showError({ details: msg as string });
                                    }),
                            );
                            // this.loading = false;
                        });
                    },
                });
            },
            error: (err) => {
                if (err.code === 'CodeMismatchException' || err.code === 'ExpiredCodeException') {
                    this.currentError = 'invalidCode';
                } else if (err.code === 'NetworkError') {
                    this.translateService.get('common.networkError').subscribe((msg) => {
                        this._toastService.showError({
                            details: msg as string,
                        });
                    });
                } else {
                    this.currentError = 'unexpected';
                }
                this.loading = false;
            },
        });
    }
    resendCode() {
        this._authService.resendCode(this._authService.getUserToValidate()!.username!).subscribe(() => {
            this._toastService.showSuccess({ details: `Successfully sent an email to the email ${this.email}` });
        });
    }

    private initializeForm() {
        this.formGroup = this._formBuilder.group({
            code: [undefined, Validators.required],
        });
    }
}
